import { floorResolution, floorResolutionValue, getBestResolution } from '@common/resolution';
import ActionCard from '@components/ActionCard';
import DesktopNavigation from '@components/DesktopNavigation';
import ExportOverlay from '@components/ExportOverlay';
import FormGroup from '@components/FormGroup';
import FormLayout from '@components/FormLayout';
import Input from '@components/Input';
import LoadingPage from '@components/LoadingPage';
import MobileNavigation from '@components/MobileNavigation';
import NumberInput from '@components/NumberInput';
import PageContent from '@components/PageContent';
import PageLayout from '@components/PageLayout';
import Select from '@components/Select';
import Switch from '@components/Switch';
import { ALLOWED_LETTERS } from '@config-web';
import { ExportFrames } from '@core/Export';
import { parseRatio } from '@core/ratio';
import useAppCapabilities from '@hooks/useAppCapabilities';
import useDiscordActivity from '@hooks/useDiscordActivity';
import useProject from '@hooks/useProject';
import useSettings from '@hooks/useSettings';
import faArrowLeft from '@icons/faArrowLeft';
import pLimit from 'p-limit';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { withTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

const GetFrameResolutionsLimit = pLimit(10);

const generateCustomUuid = (length) => {
  const array = new Uint32Array(length);
  self.crypto.getRandomValues(array);
  let out = '';
  for (let i = 0; i < length; i++) {
    out += ALLOWED_LETTERS[array[i] % ALLOWED_LETTERS.length];
  }
  return out;
};

export const GetFrameResolutions = async (frames) => {
  if (!frames || frames.length === 0) {
    return [];
  }
  const resolutions = await Promise.all(
    frames.map((frame) => {
      const metaLinkUrl = new URL(frame.metaLink, window.location.origin);
      metaLinkUrl.searchParams.set('c', 'true');
      return GetFrameResolutionsLimit(() =>
        fetch(metaLinkUrl.toString())
          .then((res) => res.json())
          .catch(() => ({ width: null, height: null }))
      );
    })
  );
  return resolutions;
};

const Export = ({ t }) => {
  const { id, track } = useParams();
  const navigate = useNavigate();
  const { project, actions: projectActions } = useProject({ id });

  const [isInfosOpened, setIsInfosOpened] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [publicCode, setPublicCode] = useState(null);
  const [resolutions, setResolutions] = useState(null);
  const [frameRenderingProgress, setFrameRenderingProgress] = useState(0);
  const [videoRenderingProgress, setVideoRenderingProgress] = useState(0);
  const [bestResolution, setBestResolution] = useState(null);

  const [searchParams] = useSearchParams();
  const { settings } = useSettings();
  const { appCapabilities } = useAppCapabilities();

  useDiscordActivity({
    actionTitle: project?.title || null,
    actionIcon: 'exporting',
    description: t('Export in progress'),
  });

  const form = useForm({
    mode: 'all',
    defaultValues: {
      mode: 'none',
      format: 'h264',
      imageResolution: 'original',
      videoResolution: null,
      framesFormat: 'original',
      duplicateFramesCopy: true,
      duplicateFramesAuto: false,
      duplicateFramesAutoNumber: 2,
      customOutputFramerate: false,
      customOutputFramerateNumber: 60,
      matchAspectRatio: true,
      compressAsZip: false,
      sendMethod: 'code',
      email: '',
      exportMaskingLayers: false,
    },
  });

  const { watch, setValue, register, handleSubmit, control } = form;
  const projectRatio = parseRatio(project?.scenes[Number(track)]?.ratio)?.value || null;

  const formats = [
    ...(appCapabilities.includes('EXPORT_VIDEO_H264') ? [{ value: 'h264', label: t('H264 (Recommended)') }] : []),
    ...(appCapabilities.includes('EXPORT_VIDEO_HEVC') ? [{ value: 'hevc', label: t('HEVC (.mp4)') }] : []),
    ...(appCapabilities.includes('EXPORT_VIDEO_PRORES') ? [{ value: 'prores', label: t('ProRes (.mov)') }] : []),
    ...(appCapabilities.includes('EXPORT_VIDEO_VP8') ? [{ value: 'vp8', label: t('VP8 (.webm)') }] : []),
    ...(appCapabilities.includes('EXPORT_VIDEO_VP9') ? [{ value: 'vp9', label: t('VP9 (.webm)') }] : []),
  ];

  const videoResolutions = [...new Set([...(bestResolution?.height ? [floorResolutionValue(bestResolution.height)] : []), 2160, 1440, 1080, 720, 480, 360, 240])]
    .filter((height) => !bestResolution || height <= floorResolutionValue(bestResolution?.height))
    .map((e) => ({ value: e, label: t('{{resolution}}p', { resolution: e }) }));

  const imageResolutions = [...new Set(['original', ...(bestResolution?.height ? [floorResolutionValue(bestResolution.height)] : []), 2160, 1440, 1080, 720, 480, 360, 240])]
    .filter((height) => height === 'original' || !bestResolution || height <= floorResolutionValue(bestResolution?.height))
    .map((e) => ({ value: e, label: e === 'original' ? t('Original (Recommended)') : t('{{resolution}}p', { resolution: e }) }));

  const framesFormats = [
    { value: 'original', label: t('Original (Recommended)') },
    { value: 'jpg', label: t('JPEG (.jpg)') },
    { value: 'png', label: t('PNG (.png)') },
    { value: 'webp', label: t('WEBP (.webp)') },
  ];

  const sendMethods = [
    { value: 'code', label: t('By code') },
    { value: 'email', label: t('By email') },
  ];

  const framesKey = JSON.stringify(project?.scenes?.[Number(track)]?.pictures);
  useEffect(() => {
    GetFrameResolutions(project?.scenes?.[Number(track)]?.pictures)
      .then((d) => {
        setResolutions(d);
      })
      .catch((err) => {
        console.error(err);
        setResolutions(null);
      });
  }, [framesKey]);

  useEffect(() => {
    (async () => {
      if (watch('mode') !== 'frames' || watch('matchAspectRatio')) {
        setBestResolution(getBestResolution(project?.scenes?.[Number(track)]?.pictures, resolutions, projectRatio));
      } else {
        setBestResolution(getBestResolution(project?.scenes?.[Number(track)]?.pictures, resolutions));
      }
    })();
  }, [framesKey, projectRatio, watch('matchAspectRatio'), watch('mode'), resolutions]);

  useEffect(() => {
    window.EAEvents('FFMPEG_PROGRESS', (evt, args) => {
      setVideoRenderingProgress(args.progress || 0);
    });
  }, []);

  // Choose the right quality when we have a status change or when resolution array is loaded
  useEffect(() => {
    setValue('imageResolution', imageResolutions.find((e) => e.value === watch('imageResolution'))?.value || imageResolutions.find((e) => e.value !== 'original')?.value || 'original');
    setValue('videoResolution', videoResolutions.find((e) => e.value === watch('videoResolution'))?.value || videoResolutions.find((e) => e.value !== 'original')?.value || 'original');
  }, [watch('mode'), JSON.stringify(videoResolutions), JSON.stringify(imageResolutions)]);

  useEffect(() => {
    if (!settings || !appCapabilities) {
      return;
    }

    (async () => {
      const bestMode = (() => {
        if (appCapabilities.includes('BACKGROUND_SYNC') && settings?.EVENT_MODE_ENABLED) {
          return 'send';
        }
        if (appCapabilities.includes('EXPORT_VIDEO')) {
          return 'video';
        }
        if (appCapabilities.includes('EXPORT_FRAMES')) {
          return 'frames';
        }

        return 'none';
      })();

      if (
        (watch('mode') === 'video' && !appCapabilities.includes('EXPORT_VIDEO')) ||
        (watch('mode') === 'frames' && !appCapabilities.includes('EXPORT_FRAMES')) ||
        (watch('mode') === 'send' && !appCapabilities.includes('BACKGROUND_SYNC')) ||
        watch('mode') === 'none'
      ) {
        setValue('mode', bestMode);
      }
    })();
  }, [appCapabilities, settings]);

  const handleBack = async () => {
    navigate(searchParams.get('back') || '/');
  };

  const primaryActions = [{ label: t('Back'), icon: faArrowLeft, onClick: handleBack }];

  if (!project || !settings || !bestResolution) {
    return (
      <>
        <LoadingPage show={true} />
        <PageLayout>
          <DesktopNavigation leftActions={primaryActions} title={t('Export')} />
          <MobileNavigation topLeftActions={primaryActions} showLeftActions={true} />
          <PageContent></PageContent>
        </PageLayout>
      </>
    );
  }

  const progress = watch('mode') === 'frames' ? Math.min(frameRenderingProgress, 1) : Math.min(frameRenderingProgress / 2, 0.5) + Math.min(videoRenderingProgress / 2, 0.5);

  const handleExport = async (data) => {
    const files = project.scenes[Number(track)].pictures;

    // Define output resolution
    let resolution = null;
    if (data.mode === 'frames') {
      if (data.imageResolution === 'original') {
        resolution = null;
      } else {
        if (data.matchAspectRatio) {
          resolution = { width: Number(data.imageResolution) * projectRatio, height: Number(data.imageResolution) };
        } else {
          resolution = { width: null, height: Number(data.imageResolution) };
        }
      }
    } else {
      if (projectRatio) {
        resolution = { width: Number(data.videoResolution) * projectRatio, height: Number(data.videoResolution) };
      } else {
        const maxResolution = getBestResolution(files, resolutions);
        resolution = { width: (Number(data.videoResolution) * maxResolution.width) / maxResolution.height, height: Number(data.videoResolution) };
      }
    }

    resolution = floorResolution(resolution);

    setIsInfosOpened(true);
    setIsExporting(true);
    setFrameRenderingProgress(0);
    setVideoRenderingProgress(0);

    const newCode = data.mode === 'send' ? await generateCustomUuid(8) : null;

    if (data.mode === 'send' && data.sendMethod === 'code') {
      setPublicCode(newCode);
      if (!project.title) {
        projectActions.rename(newCode);
      }
    } else {
      setPublicCode(null);
    }

    // Ask user to define output path
    const outputPath =
      data.mode === 'send'
        ? null
        : await window.EA('EXPORT_SELECT_PATH', {
            type: data.mode === 'video' ? 'FILE' : 'FOLDER',
            format: data.format,
            translations: {
              EXPORT_FRAMES: t('Export animation frames'),
              EXPORT_VIDEO: t('Export as video'),
              DEFAULT_FILE_NAME: t('video'),
              EXT_NAME: t('Video file'),
            },
            compress_as_zip: data.mode === 'frames' ? data.compressAsZip && appCapabilities.includes('EXPORT_FRAMES_ZIP') : false,
          });

    // Cancel if result is null, (dialog closed)
    if (data.mode !== 'send' && outputPath === null) {
      setIsInfosOpened(false);
      setIsExporting(false);
      return;
    }

    const createBuffer = async (bufferId, buffer) => {
      await window.EA('EXPORT_BUFFER', {
        project_id: id,
        buffer_id: bufferId,
        buffer,
      });
    };

    const exportSettings = {
      duplicateFramesCopy: data.duplicateFramesCopy,
      duplicateFramesAuto: data.mode === 'send' ? true : data.duplicateFramesAuto,
      duplicateFramesAutoNumber: data.mode === 'send' ? Math.ceil(project?.scenes?.[Number(track)]?.framerate / 2) : data.duplicateFramesAutoNumber,
      forceFileExtension: data.mode === 'frames' ? (data.framesFormat === 'original' ? undefined : data.framesFormat) : 'jpg',
      resolution,
      exportMaskingLayers: data.mode === 'frames' && !!data?.exportMaskingLayers,
    };

    // Track export
    window.track('project_exported', { projectId: project.id, ...data, ...exportSettings });

    // Compute all frames
    const frames = await ExportFrames(id, Number(track), files, exportSettings, (p) => setFrameRenderingProgress(p), createBuffer);

    // Save frames / video on the disk
    await window.EA('EXPORT', {
      frames: frames.map(({ mimeType, bufferId, ...e }) => ({ ...e, buffer_id: bufferId, mime_type: mimeType })),
      output_path: outputPath,
      mode: data.mode,
      format: data.format,
      framerate: project?.scenes?.[Number(track)]?.framerate,
      frames_format: data.framesFormat,
      custom_output_framerate: data.customOutputFramerate,
      custom_output_framerate_number: data.customOutputFramerateNumber,
      project_id: id,
      track_id: track,
      compress_as_zip: data.mode === 'frames' ? data.compressAsZip && appCapabilities.includes('EXPORT_FRAMES_ZIP') : false,
      endpoint: settings.EVENT_API,
      send_method: data.sendMethod,
      email: data.mode === 'send' && data.sendMethod === 'email' ? data.email : '',
      public_code: data.mode === 'send' && data.sendMethod === 'code' ? newCode : '',
      event_key: settings.EVENT_KEY,
      exportMaskingLayers: !!data?.exportMaskingLayers,
    });

    setIsExporting(false);
  };

  const handleModeChange = (v) => () => {
    setValue('mode', v);
  };

  return (
    <>
      <LoadingPage show={!settings} />
      <PageLayout hasMobileLeftBar={true}>
        <DesktopNavigation leftActions={primaryActions} title={t('Export')} />
        <MobileNavigation topLeftActions={primaryActions} showLeftActions={true} />
        <PageContent>
          {settings && (
            <form id="export">
              <FormLayout>
                <div style={{ display: 'flex', gap: 'var(--space-medium)', justifyContent: 'center' }}>
                  {appCapabilities.includes('EXPORT_VIDEO') && <ActionCard icon="VIDEO" title={t('Export as video')} onClick={handleModeChange('video')} selected={watch('mode') === 'video'} />}
                  {appCapabilities.includes('EXPORT_FRAMES') && (
                    <ActionCard icon="FRAMES" title={t('Export animation frames')} onClick={handleModeChange('frames')} selected={watch('mode') === 'frames'} />
                  )}
                  {appCapabilities.includes('BACKGROUND_SYNC') && settings.EVENT_MODE_ENABLED && settings.EVENT_API && (
                    <ActionCard icon="SEND" title={t('Upload the video')} onClick={handleModeChange('send')} selected={watch('mode') === 'send'} />
                  )}
                </div>

                {['video', 'send'].includes(watch('mode')) && (
                  <FormGroup label={t('Video format')} description={t('The exported video format')}>
                    <Select control={control} options={formats} register={register('format')} />
                  </FormGroup>
                )}

                {watch('mode') === 'frames' && (
                  <FormGroup label={t('Frames format')} description={t('The format of exported frames')}>
                    <Select control={control} options={framesFormats} register={register('framesFormat')} />
                  </FormGroup>
                )}

                {['video', 'send'].includes(watch('mode')) && (
                  <FormGroup label={t('Video resolution')} description={t('The exported video resolution')}>
                    <Select control={control} options={videoResolutions} register={register('videoResolution')} />
                  </FormGroup>
                )}

                {['frames'].includes(watch('mode')) && (
                  <FormGroup label={t('Frames resolution')} description={t('The exported frames resolution')}>
                    <Select control={control} options={imageResolutions} register={register('imageResolution')} />
                  </FormGroup>
                )}

                {watch('mode') === 'frames' && watch('imageResolution') !== 'original' && (
                  <FormGroup label={t('Use project ratio')} description={t('Normalize all the frames to match the project aspect ratio')}>
                    <div>
                      <Switch register={register('matchAspectRatio')} />
                    </div>
                  </FormGroup>
                )}

                {watch('mode') === 'frames' && (
                  <FormGroup label={t('Export masking layers')} description={t('Export the layers of masked frames')}>
                    <div>
                      <Switch register={register('exportMaskingLayers')} />
                    </div>
                  </FormGroup>
                )}

                {['video'].includes(watch('mode')) && (
                  <FormGroup label={t('Custom video output framerate')} description={t('Change the exported video framerate (This is not your animation framerate)')}>
                    <div style={{ display: 'inline-block' }}>
                      <Switch register={register('customOutputFramerate')} />
                    </div>
                    {watch('customOutputFramerate') && (
                      <div style={{ display: 'inline-block', marginLeft: 'var(--space-big)' }}>
                        <NumberInput register={register('customOutputFramerateNumber')} min={watch('framerate')} max={240} />
                      </div>
                    )}
                  </FormGroup>
                )}

                {['video', 'frames'].includes(watch('mode')) && (
                  <FormGroup label={t('Duplicate first and last frames')} description={t('Automatically duplicate the first and last frames')}>
                    <div style={{ display: 'inline-block' }}>
                      <Switch register={register('duplicateFramesAuto')} />
                    </div>
                    {watch('duplicateFramesAuto') && (
                      <div style={{ display: 'inline-block', marginLeft: 'var(--space-big)' }}>
                        <NumberInput register={register('duplicateFramesAutoNumber')} min={2} max={10} />
                      </div>
                    )}
                  </FormGroup>
                )}

                {['send'].includes(watch('mode')) && (
                  <>
                    <FormGroup label={t('Send method')} description={t('The way the user will retrieve their video')}>
                      <Select control={control} options={sendMethods} register={register('sendMethod')} />
                    </FormGroup>
                    {watch('sendMethod') === 'email' && (
                      <FormGroup label={t('Email address')} description={t('The email address to send the video to')}>
                        <Input control={control} register={register('email')} />
                      </FormGroup>
                    )}
                  </>
                )}

                {watch('mode') === 'frames' && (
                  <FormGroup label={t('Duplicate frames')} description={t('Copies several times the duplicated frames')}>
                    <div>
                      <Switch register={register('duplicateFramesCopy')} />
                    </div>
                  </FormGroup>
                )}

                {['frames'].includes(watch('mode')) && appCapabilities.includes('EXPORT_FRAMES_ZIP') && (
                  <FormGroup label={t('ZIP')} description={t('Export frames in a ZIP file')}>
                    <Switch register={register('compressAsZip')} />
                  </FormGroup>
                )}

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <ActionCard title={t('Export')} onClick={handleSubmit(handleExport)} sizeAuto secondary disabled={isInfosOpened} />
                </div>
              </FormLayout>
            </form>
          )}
        </PageContent>
      </PageLayout>
      {isInfosOpened && (
        <ExportOverlay
          showNewProjectButton={watch('mode') === 'send'}
          publicCode={watch('sendMethod') === 'code' ? publicCode : null}
          isExporting={isExporting}
          progress={progress}
          onCancel={() => {
            setIsInfosOpened(false);
            setIsExporting(false);
          }}
        />
      )}
    </>
  );
};

export default withTranslation()(Export);
